import React, { memo } from "react";
import { useFormContext, Controller } from "react-hook-form";
import { MDBRow, MDBCol } from "mdbreact";

import { Label, Input } from "abs-ui-components";

import {
	INPUT_STEP_VALUE,
	preventScientificNotation,
	roundOff,
} from "../../../services/utilities";

const MainEngineFormGroup = (props) => {
	const {
		control,
		formState: { errors },
		setValue,
		trigger,
		getValues,
	} = useFormContext();

	const handleBlur = async (field, value) => {
		const newValue = value && value.trim() !== "" ? roundOff(value) : "";
		setValue(field, newValue);
		await trigger(field);
		const mainEngineValues = getValues("mainEngineList");
		const sumOfMainEngineList = Object.values(mainEngineValues).reduce(
			(acc, val) => {
				const numericValue = parseFloat(val);
				return !isNaN(numericValue) && val.trim() !== ""
					? acc + numericValue
					: acc;
			},
			0
		);
		
		props.setSumofMainEnginePowers(sumOfMainEngineList);
	};

	const configuredMainEngineData =
		props?.vesselData?.VesselEnginePowerDtls?.filter((item) => {
			return item.EngineType === "ME";
		});

	const transformedData = configuredMainEngineData?.reduce((result, item) => {
		const key = item.EngineName;
		const value = item.OutputPower;
		result[key] = value;
		return result;
	}, {});

	return (
		<div className="container-fluid p-0 m-0">
			<MDBRow>
				<MDBCol md="3" className="mt-3">
					<Label
						required
						bold
						name="ME1 Power (kW)"
						className="mb-1"
					/>
					<Controller
						name="mainEngineList.ME1"
						control={control}
						rules={
							props?.regulations?.trim() !== "EUMRV" &&
							props?.regulations?.trim() !== "UKMRV" &&
							props?.regulations?.trim() !== "DCS"
								? {
										required: "ME1 power is required",
										min: {
											value: Number.EPSILON,
											message:
												"Please enter a value greater than zero.",
										},
								  }
								: {}
						}
						defaultValue={
							props?.vesselMstId
								? transformedData?.ME1 != null &&
								  transformedData.ME1 > 0
									? roundOff(transformedData?.ME1)
									: ""
								: ""
						}
						render={({ field }) => (
							<Input
								id="main_engine_me1_power"
								autoFocus={false}
								value={field.value}
								onChange={field.onChange}
								type="number"
								className="input-height"
								required
								step={INPUT_STEP_VALUE}
								onInput={(e) => {
									if (e.target.value.length > 7) {
										e.target.value = e.target.value.slice(
											0,
											7
										);
									}
								}}
								onBlur={(e) =>
									handleBlur(field.name, field.value)
								}
								onKeyDown={(e) => {
									preventScientificNotation(e);
									// Prevent up and down arrow keys
									if (
										e.key === "ArrowUp" ||
										e.key === "ArrowDown"
									) {
										e.preventDefault();
									}
								}}
								onWheel={(e) => e.target.blur()}
								maxLength={7}
								disabled={
									!props?.isEnrollVessel &&
									props?.vesselMstId &&
									(props?.regulations?.trim() === "EUMRV" ||
										"UKMRV" ||
										"DCS")
								}
							/>
						)}
					/>
					{errors.mainEngineList && errors.mainEngineList.ME1 && (
						<div className="error-msg">
							{errors.mainEngineList.ME1.message}
						</div>
					)}
				</MDBCol>

				<MDBCol md="3" className="mt-3">
					<Label bold name="ME2 Power (kW)" className="mb-1" />
					<Controller
						name="mainEngineList.ME2"
						control={control}
						defaultValue={
							props?.vesselMstId
								? transformedData?.ME2 === 0
									? ""
									: roundOff(transformedData?.ME2)
								: ""
						}
						render={({ field }) => (
							<Input
								id="main_engine_me2_power"
								disabled={
									!props?.isEnrollVessel &&
									props?.vesselMstId &&
									(props?.regulations?.trim() === "EUMRV" ||
										"UKMRV" ||
										"DCS")
								}
								autoFocus={false}
								value={field.value}
								onChange={field.onChange}
								type="number"
								min="1"
								step={INPUT_STEP_VALUE}
								className="input-height"
								onBlur={(e) =>
									handleBlur(field.name, field.value)
								}
								onInput={(e) => {
									if (e.target.value.length > 7) {
										e.target.value = e.target.value.slice(
											0,
											7
										);
									}
								}}
								onKeyDown={(e) => {
									preventScientificNotation(e);
									// Prevent up and down arrow keys
									if (
										e.key === "ArrowUp" ||
										e.key === "ArrowDown"
									) {
										e.preventDefault();
									}
								}}
								onWheel={(e) => e.target.blur()}
								maxLength={7}
							/>
						)}
					/>
				</MDBCol>

				<MDBCol md="3" className="mt-3">
					<Label bold name="ME3 Power (kW)" className="mb-1" />
					<Controller
						name="mainEngineList.ME3"
						control={control}
						defaultValue={
							props?.vesselMstId
								? transformedData?.ME3 === 0
									? ""
									: roundOff(transformedData?.ME3)
								: ""
						}
						render={({ field }) => (
							<Input
								id="main_engine_me3_power"
								disabled={
									!props?.isEnrollVessel &&
									props?.vesselMstId &&
									(props?.regulations?.trim() === "EUMRV" ||
										"UKMRV" ||
										"DCS")
								}
								autoFocus={false}
								value={field.value}
								onChange={field.onChange}
								type="number"
								min="1"
								step={INPUT_STEP_VALUE}
								className="input-height"
								onBlur={(e) =>
									handleBlur(field.name, field.value)
								}
								onKeyDown={(e) => {
									preventScientificNotation(e);
									// Prevent up and down arrow keys
									if (
										e.key === "ArrowUp" ||
										e.key === "ArrowDown"
									) {
										e.preventDefault();
									}
								}}
								onWheel={(e) => e.target.blur()}
								maxLength={7}
								onInput={(e) => {
									if (e.target.value.length > 7) {
										e.target.value = e.target.value.slice(
											0,
											7
										);
									}
								}}
							/>
						)}
					/>
				</MDBCol>

				<MDBCol md="3" className="mt-3">
					<Label bold name="ME4 Power (kW)" className="mb-1" />
					<Controller
						name="mainEngineList.ME4"
						control={control}
						defaultValue={
							props?.vesselMstId
								? transformedData?.ME4 === 0
									? ""
									: roundOff(transformedData?.ME4)
								: ""
						}
						render={({ field }) => (
							<Input
								id="main_engine_me4_power"
								disabled={
									!props?.isEnrollVessel &&
									props?.vesselMstId &&
									(props?.regulations?.trim() === "EUMRV" ||
										"UKMRV" ||
										"DCS")
								}
								autoFocus={false}
								value={field.value}
								onChange={field.onChange}
								type="number"
								min="1"
								step={INPUT_STEP_VALUE}
								className="input-height"
								onBlur={(e) =>
									handleBlur(field.name, field.value)
								}
								onKeyDown={(e) => {
									preventScientificNotation(e);
									// Prevent up and down arrow keys
									if (
										e.key === "ArrowUp" ||
										e.key === "ArrowDown"
									) {
										e.preventDefault();
									}
								}}
								onWheel={(e) => e.target.blur()}
								maxLength={7}
								onInput={(e) => {
									if (e.target.value.length > 7) {
										e.target.value = e.target.value.slice(
											0,
											7
										);
									}
								}}
							/>
						)}
					/>
					{errors.mainEngine && errors.mainEngine.ME4 && (
						<div className="error-msg">
							{errors.mainEngine.ME4.message}
						</div>
					)}
				</MDBCol>
			</MDBRow>
			<MDBRow>
				<MDBCol md="3" className="mt-3">
					<Label bold name="ME5 Power (kW)" className="mb-1" />
					<Controller
						name="mainEngineList.ME5"
						control={control}
						defaultValue={
							props?.vesselMstId
								? transformedData?.ME5 === 0
									? ""
									: roundOff(transformedData?.ME5)
								: ""
						}
						render={({ field }) => (
							<Input
								id="main_engine_me5_power"
								disabled={
									!props?.isEnrollVessel &&
									props?.vesselMstId &&
									(props?.regulations?.trim() === "EUMRV" ||
										"UKMRV" ||
										"DCS")
								}
								autoFocus={false}
								value={field.value}
								onChange={field.onChange}
								type="number"
								min="1"
								step={INPUT_STEP_VALUE}
								className="input-height"
								onBlur={(e) =>
									handleBlur(field.name, field.value)
								}
								onKeyDown={(e) => {
									preventScientificNotation(e);
									// Prevent up and down arrow keys
									if (
										e.key === "ArrowUp" ||
										e.key === "ArrowDown"
									) {
										e.preventDefault();
									}
								}}
								onWheel={(e) => e.target.blur()}
								maxLength={7}
								onInput={(e) => {
									if (e.target.value.length > 7) {
										e.target.value = e.target.value.slice(
											0,
											7
										);
									}
								}}
							/>
						)}
					/>
				</MDBCol>

				<MDBCol md="3" className="mt-3">
					<Label bold name="ME6 Power (kW)" className="mb-1" />
					<Controller
						name="mainEngineList.ME6"
						control={control}
						defaultValue={
							props?.vesselMstId
								? transformedData?.ME6 === 0
									? ""
									: roundOff(transformedData?.ME6)
								: ""
						}
						render={({ field }) => (
							<Input
								id="main_engine_me6_power"
								disabled={
									!props?.isEnrollVessel &&
									props?.vesselMstId &&
									(props?.regulations?.trim() === "EUMRV" ||
										"UKMRV" ||
										"DCS")
								}
								autoFocus={false}
								value={field.value}
								onChange={field.onChange}
								type="number"
								min="1"
								step={INPUT_STEP_VALUE}
								className="input-height"
								onBlur={(e) =>
									handleBlur(field.name, field.value)
								}
								onKeyDown={(e) => {
									preventScientificNotation(e);
									// Prevent up and down arrow keys
									if (
										e.key === "ArrowUp" ||
										e.key === "ArrowDown"
									) {
										e.preventDefault();
									}
								}}
								onWheel={(e) => e.target.blur()}
								maxLength={7}
								onInput={(e) => {
									if (e.target.value.length > 7) {
										e.target.value = e.target.value.slice(
											0,
											7
										);
									}
								}}
							/>
						)}
					/>
				</MDBCol>

				<MDBCol md="3" className="mt-3">
					<Label bold name="ME7 Power (kW)" className="mb-1" />
					<Controller
						name="mainEngineList.ME7"
						control={control}
						defaultValue={
							props?.vesselMstId
								? transformedData?.ME7 === 0
									? ""
									: roundOff(transformedData?.ME7)
								: ""
						}
						render={({ field }) => (
							<Input
								id="main_engine_me7_power"
								disabled={
									!props?.isEnrollVessel &&
									props?.vesselMstId &&
									(props?.regulations?.trim() === "EUMRV" ||
										"UKMRV" ||
										"DCS")
								}
								autoFocus={false}
								value={field.value}
								onChange={field.onChange}
								type="number"
								min="1"
								step={INPUT_STEP_VALUE}
								className="input-height"
								onBlur={(e) =>
									handleBlur(field.name, field.value)
								}
								onKeyDown={(e) => {
									preventScientificNotation(e);
									// Prevent up and down arrow keys
									if (
										e.key === "ArrowUp" ||
										e.key === "ArrowDown"
									) {
										e.preventDefault();
									}
								}}
								onWheel={(e) => e.target.blur()}
								maxLength={7}
								onInput={(e) => {
									if (e.target.value.length > 7) {
										e.target.value = e.target.value.slice(
											0,
											7
										);
									}
								}}
							/>
						)}
					/>
				</MDBCol>

				<MDBCol md="3" className="mt-3">
					<Label bold name="ME8 Power (kW)" className="mb-1" />
					<Controller
						name="mainEngineList.ME8"
						control={control}
						defaultValue={
							props?.vesselMstId
								? transformedData?.ME8 === 0
									? ""
									: roundOff(transformedData?.ME8)
								: ""
						}
						render={({ field }) => (
							<Input
								id="main_engine_me8_power"
								disabled={
									!props?.isEnrollVessel &&
									props?.vesselMstId &&
									(props?.regulations?.trim() === "EUMRV" ||
										"UKMRV" ||
										"DCS")
								}
								autoFocus={false}
								value={field.value}
								onChange={field.onChange}
								type="number"
								min="1"
								step={INPUT_STEP_VALUE}
								className="input-height"
								onBlur={(e) =>
									handleBlur(field.name, field.value)
								}
								onKeyDown={(e) => {
									preventScientificNotation(e);
									// Prevent up and down arrow keys
									if (
										e.key === "ArrowUp" ||
										e.key === "ArrowDown"
									) {
										e.preventDefault();
									}
								}}
								onWheel={(e) => e.target.blur()}
								maxLength={7}
								onInput={(e) => {
									if (e.target.value.length > 7) {
										e.target.value = e.target.value.slice(
											0,
											7
										);
									}
								}}
							/>
						)}
					/>
				</MDBCol>
			</MDBRow>
		</div>
	);
};

export default memo(MainEngineFormGroup);
